import {
  WorkflowMessage,
  WorkflowErrorMessage,
  formattedWorkflowMessage
} from "@/constants/workflows/workflows"
import { WorkflowAPI } from "@/API/Workflow"
import { AppDispatch } from "@/redux"
import { appActions } from "@/redux/slice/appSlice"
import { workflowActions } from "@/redux/slice/workflowSlice"
import { WorkflowCreateRequestBodyV2 } from "@/types/WorkflowCreate/workflowCreate"
import { showNotification } from "./notificationService"
import { WorkflowUpdateRequestBodyV2 } from "@/types/WorkflowCreate/workflowUpdateV2"
import { TCSAPIResponse } from "@/types/types"

export const getAllWorkflows = (
  token: string
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await WorkflowAPI.getAll(token)
  if (resp.data) {
    dispatch(workflowActions.setWorkflows(resp.data))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const getSingleWorkflow = (
  token: string,
  workflowId: string
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await WorkflowAPI.getSingleWorkflow(token, workflowId)
  dispatch(appActions.setLoading(false))
  return resp
}

function isQuotaExceededError(err: string | undefined): boolean {
  if (!err) return false
  return /QUOTA_EXCEEDED/i.test(err) || /quota\s*exceeded/i.test(err)
}

export const createWorkflow = (
  token: string,
  body: WorkflowCreateRequestBodyV2,
  fullFetch: boolean = true
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await WorkflowAPI.createWorkflow(
    token,
    body
  )
  const errForNotification = isQuotaExceededError(resp.err)
    ? WorkflowErrorMessage.QUOTA_EXCEEDED
    : resp.err
  showNotification(
    { data: resp.data as unknown as TCSAPIResponse, err: errForNotification },
    WorkflowMessage.CREATE_SUCCESSFUL,
    formattedWorkflowMessage
  )
  if (resp.data && fullFetch) {
    await dispatch(getAllWorkflows(token))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const saveDraftWorkflow = (
  token: string,
  body: WorkflowCreateRequestBodyV2,
  fullFetch: boolean = true
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await WorkflowAPI.saveDraftWorkflow(
    token,
    body
  )
  showNotification(
    { data: resp.data as unknown as TCSAPIResponse, err: resp.err },
    WorkflowMessage.CREATE_SUCCESSFUL,
    formattedWorkflowMessage
  )
  if (resp.data && fullFetch) {
    await dispatch(getAllWorkflows(token))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const publishDraftWorkflow = (
  token: string,
  workflowId: string,
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await WorkflowAPI.publishDraftWorkflow(
    token,
    workflowId
  )
  const errForNotification = isQuotaExceededError(resp.err)
    ? WorkflowErrorMessage.QUOTA_EXCEEDED
    : resp.err
  showNotification(
    { data: resp.data as unknown as TCSAPIResponse, err: errForNotification },
    WorkflowMessage.UPDATE_SUCCESSFUL,
    formattedWorkflowMessage
  )
  if (resp.data) {
    await dispatch(getAllWorkflows(token))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const updateWorkflow = (
  token: string,
  workflowId: string,
  body: WorkflowUpdateRequestBodyV2
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await WorkflowAPI.updateWorkflow(
    token,
    workflowId,
    body
  )
  showNotification(
    resp,
    WorkflowMessage.UPDATE_SUCCESSFUL,
    formattedWorkflowMessage
  )
  if (resp.data) {
    await dispatch(getAllWorkflows(token))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const updateDraftWorkflow = (
  token: string,
  workflowId: string,
  body: WorkflowUpdateRequestBodyV2
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await WorkflowAPI.updateDraftWorkflow(
    token,
    workflowId,
    body
  )
  showNotification(
    resp,
    WorkflowMessage.UPDATE_SUCCESSFUL,
    formattedWorkflowMessage
  )
  if (resp.data) {
    await dispatch(getAllWorkflows(token))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const changeWorkflowStatus = (
  token: string,
  workflowId: string,
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await WorkflowAPI.changeWorkflowStatus(
    token,
    workflowId
  )

  if (resp.data) {
    await dispatch(getAllWorkflows(token))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const deleteWorkflow = (
  token: string,
  workflowId: string,
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await WorkflowAPI.deleteWorkflow(
    token,
    workflowId
  )
  showNotification(
    resp,
    WorkflowMessage.DELETE_SUCCESSFUL,
    formattedWorkflowMessage
  )

  if (resp.data) {
    await dispatch(getAllWorkflows(token))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const runWorkflow = (
  token: string,
  workflowId: string,
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await WorkflowAPI.runWorkflow(
    token,
    workflowId
  )
  showNotification(
    resp,
    WorkflowMessage.RUN_SUCCESSFUL,
    formattedWorkflowMessage
  )

  if (resp.data) {
    await dispatch(getAllWorkflows(token))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const getActionsAndSubjectsByApp = (
  token: string,
  appId: string
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await WorkflowAPI.getActionsAndSubjectsByApp(
    token,
    appId
  )
  dispatch(appActions.setLoading(false))
  return resp
}
